import { useMemo, type ReactNode } from 'react'
import Select from 'react-select'
import { Col } from 'reactstrap'
import { useProductAvailableOptions } from '../../../../hooks/useProductAvailableOptions'
import dollar_price from '../../../general/DollarPrice'
import { configurableSelectStyles } from '../../configurableSelectStyles'
import {
  resolveConfigurationSelection,
  type ConfigurationOption,
  type ConfigurationSelections,
  type ConfigurationSet
} from '../../configurationSelection'

type BundleProduct = {
  product_id: number
  bundle_group_product_id: number
  product_sku: string
  nested_data: ConfigurationSet[]
  variants: ConfigurationSet[]
  allow_backorder_inventory?: boolean
}

type Props = {
  variant: ConfigurationSet
  product: BundleProduct
  sets: ConfigurationSet[]
  selections: ConfigurationSelections
  selectOption: (variantId: number, optionId: number) => void
  currentLocation: { show_price: boolean }
  availableSkus: string[]
}

const ConfigurableProduct = ({
  variant,
  product,
  sets,
  selections,
  selectOption,
  currentLocation,
  availableSkus
}: Props) => {
  const productAvailableSkus = useMemo(() => ({ ...product, available_skus: availableSkus }), [product, availableSkus])
  const availableOptions = useProductAvailableOptions(productAvailableSkus, selections)

  const suffix = (option: ConfigurationOption) => {
    if ('allAvailable' in availableOptions && availableOptions.allAvailable) return ''
    if (!availableOptions[variant.variant_id]?.has(option.sku_code)) {
      const optimisticSelection = { ...selections, [variant.variant_id]: option }
      const prospectiveSku = `${product.product_sku}${resolveConfigurationSelection(sets, optimisticSelection).skuSuffix}`
      return availableSkus.includes(prospectiveSku) ? ' - Sold out' : ' - Unavailable'
    }
    return ''
  }

  const selectOptions = (variant.nested_data || []).map(option => ({
    value: option.id,
    label: option.name,
    option
  }))
  const selectedOption = selections[variant.variant_id]
  const selectedSelectOption = selectedOption
    ? selectOptions.find(selectOption => selectOption.value === selectedOption.id)
    : null

  const formatSelectOptionLabel = (selectOption: (typeof selectOptions)[number]) => {
    const option = selectOption.option
    let optionInputLabel: ReactNode = option.name
    if (option.has_price_modifier && currentLocation.show_price && option.price_modifier) {
      optionInputLabel = (
        <>
          {optionInputLabel} + {dollar_price(option.price_modifier.price_modifier_cents)}
        </>
      )
    }
    return (
      <>
        {optionInputLabel}
        {suffix(option)}
      </>
    )
  }

  return (
    <Col className="mf-mt-1 configurable-select-option" xs="12" md="6">
      <b>{variant.display_name || variant.name}</b>
      <Select
        className="mf-mt-1 mf-mb-0 configurable-option-select"
        classNamePrefix="configurable-option-select"
        isClearable={false}
        options={selectOptions}
        value={selectedSelectOption || null}
        placeholder="Please select..."
        onChange={choice => choice && selectOption(variant.variant_id, choice.value)}
        formatOptionLabel={formatSelectOptionLabel}
        styles={configurableSelectStyles}
      />
    </Col>
  )
}

export default ConfigurableProduct
